/**
 * Utilitários para instalar extensões de devices de forma robusta.
 *
 * Contexto:
 * - Dependendo da versão do VM, `device.deviceExtensions` pode vir como:
 *   - lista de IDs (string), ou
 *   - lista de descritores/objetos (ex.: {extensionId, ...}).
 * - `vm.extensionManager.getDeviceExtensionsList()` normalmente retorna a lista
 *   completa de descritores disponíveis.
 *
 * Objetivo:
 * - Construir uma lista de extensões para instalar que funcione tanto com IDs
 *   quanto com descritores, priorizando descritores quando disponíveis.
 * Observação importante (openblock-vm):
 * - `getDeviceExtensionsList()` NÃO lista as categorias built-in do device Arduino
 *   (Pins/Serial/Data). Essas categorias vêm do `getInfo()` do próprio device e seus
 *   IDs finais são gerados pelo VM (ex.: `arduino_serial`, `arduino_data`), e NÃO
 *   são carregados via `loadDeviceExtension`.
 *
 * Ainda assim, `getDeviceExtensionsList()` pode listar extensões adicionais que
 * complementam o device. Aqui fazemos uma resolução robusta e adicionamos um
 * fallback opcional para IDs equivalentes quando o VM usar nomes diferentes.
 */

export const ARDUINO_EXTENSION_FALLBACKS = {
    serial: [/arduino.*(serial|uart)/i, /(serial|uart).*arduino/i],
    data: [/arduino.*data/i, /data.*arduino/i]
};

export const getExtensionId = extension => {
    if (!extension) return null;
    if (typeof extension === 'string') return extension;
    if (typeof extension !== 'object') return null;
    return (
        extension.extensionId ||
        extension.id ||
        extension.deviceExtensionId ||
        extension.extensionID ||
        null
    );
};

/**
 * @param {object} params
 * @param {string|null|undefined} params.deviceId
 * @param {Array<string|object>|null|undefined} params.deviceExtensions
 * @param {Array<object>|null|undefined} params.availableExtensions - retorno de getDeviceExtensionsList()
 * @returns {Array<string|object>}
 */
export const resolveDeviceExtensionsToInstall = ({deviceId, deviceExtensions, availableExtensions}) => {
    const initial = Array.isArray(deviceExtensions) ? deviceExtensions : [];
    const available = Array.isArray(availableExtensions) ? availableExtensions : [];

    const availableById = new Map();
    for (const ext of available) {
        const id = getExtensionId(ext);
        if (id) availableById.set(id, ext);
    }

    const result = [];
    const seen = new Set();

    const push = entry => {
        const id = getExtensionId(entry);
        if (!id) return;
        if (seen.has(id)) return;
        seen.add(id);
        result.push(entry);
    };

    // Preserva a ordem original do device, mas troca por descritor se existir.
    for (const entry of initial) {
        const id = getExtensionId(entry);
        if (!id) continue;
        push(availableById.get(id) || entry);
    }

    // Fallback: se existirem extensões "equivalentes" no catálogo do VM, inclui.
    // Importante: só adiciona se estiverem no catálogo (availableById), para evitar
    // tentar carregar IDs inexistentes.
    const isArduino = typeof deviceId === 'string' && deviceId.toLowerCase().startsWith('arduino');
    if (isArduino && available.length > 0) {
        const availableIds = Array.from(availableById.keys());
        const pickByPatterns = patterns => {
            for (const pattern of patterns) {
                const matchId = availableIds.find(id => pattern.test(id));
                if (matchId) return matchId;
            }
            return null;
        };

        const serialId = pickByPatterns(ARDUINO_EXTENSION_FALLBACKS.serial);
        if (serialId && !seen.has(serialId)) push(availableById.get(serialId));

        const dataId = pickByPatterns(ARDUINO_EXTENSION_FALLBACKS.data);
        if (dataId && !seen.has(dataId)) push(availableById.get(dataId));
    }

    return result;
};

/**
 * Versão "IDs apenas" — útil para APIs que esperam `extensionId` (ex.: loadDeviceExtension).
 *
 * @param {object} params
 * @param {string|null|undefined} params.deviceId
 * @param {Array<string|object>|null|undefined} params.deviceExtensions
 * @param {Array<object>|null|undefined} params.availableExtensions - retorno de getDeviceExtensionsList()
 * @returns {Array<string>}
 */
export const resolveDeviceExtensionIdsToInstall = ({deviceId, deviceExtensions, availableExtensions}) => {
    const resolved = resolveDeviceExtensionsToInstall({deviceId, deviceExtensions, availableExtensions});
    const ids = [];
    const seen = new Set();
    for (const entry of resolved) {
        const id = getExtensionId(entry);
        if (!id) continue;
        if (seen.has(id)) continue;
        seen.add(id);
        ids.push(id);
    }
    return ids;
};

